import { useModalStore } from '@custom/ui/utils/ModalStore';

import {
  isConsentExemptPath,
  loadCookieConsent,
} from './src/utils/cookieConsent';
import { PIWIK_ROUTE_CHANGE_EVENT } from './src/utils/piwik';

const consentGateResolved = (state) =>
  state.restrictionsInitialized && state.state === 'closed';

// The privacy policy must stay readable before any consent decision, so the
// consent banner never shows there. This intentionally mirrors (but is a
// separate policy from) the non-mandatory domicile modal exception for the
// same page in packages/ui's accessRestriction.ts. The trailing slash is
// tolerated because static hosting may serve either form of the URL.
const onConsentExemptPage = () => isConsentExemptPath(window.location.pathname);

export const onClientEntry = () => {
  // Load the Cookie Information script only once the domicile gate is
  // resolved for THIS page load: either the visitor completes the modal, or
  // restrictions initialise with nothing to show (stored profile, or a page
  // with a non-mandatory modal). `restrictionsInitialized` is never
  // persisted, so a fresh hydration can never fire this early. The initial
  // getState() check is defensive: that branch cannot be taken in practice.
  //
  // On the consent-exempt privacy policy page the gate resolves but the
  // script is NOT loaded and the subscription stays alive: every page mount
  // calls setRestrictions (which always notifies subscribers), so navigating
  // away re-evaluates the gate against the new pathname and loads it then.
  const maybeLoadCookieConsent = (state) => {
    if (consentGateResolved(state) && !onConsentExemptPage()) {
      loadCookieConsent();
      return true;
    }
    return false;
  };

  if (maybeLoadCookieConsent(useModalStore.getState())) {
    return;
  }
  const unsubscribe = useModalStore.subscribe((state) => {
    if (maybeLoadCookieConsent(state)) {
      unsubscribe();
    }
  });
};

export const shouldUpdateScroll = (args) => {
  // Tell Gatsby to only update scroll position if the pathname or hash has changed.
  // If only the search has changed (e.g. when a search form is submitted),
  // the scroll position should remain the same.
  const current = args.routerProps.location;
  const previous = args.prevRouterProps?.location;
  return (
    current.pathname !== previous?.pathname || current.hash !== previous?.hash
  );
};

let initialRouteHandled = false;

export const onRouteUpdate = ({ location }) => {
  // Gatsby calls onRouteUpdate on the initial render as well, where the Piwik
  // container tracks the page view itself. Skipping it avoids double counting.
  if (!initialRouteHandled) {
    initialRouteHandled = true;
    return;
  }

  // Gatsby commits the new document title after this callback runs, so give it
  // a moment - otherwise the previous page's title is reported. 32ms is the
  // same delay `gatsby-plugin-google-gtag` uses in its `sendPageView`
  // fallback (its comment calls it "simulate 2 rAF calls"). A plain
  // setTimeout is preferred over requestAnimationFrame here because rAF does
  // not fire in a background tab, which would drop the event entirely.
  setTimeout(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: PIWIK_ROUTE_CHANGE_EVENT,
      virtualPageUrl: `${location.pathname}${location.search}${location.hash}`,
      virtualPageTitle: document.title,
    });
  }, 32);
};
